import { useEffect, useRef } from 'react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { TaskResponseDto } from '@/types/task';
import { BASE_URL } from '@/utils/constants/apiEndpoints';

export const USER_PRESENCE_STATUS = {
  AVAILABLE: 'available',
  BUSY: 'busy',
  EMR_WORKFLOW: 'emr_workflow',
  EXPORTING: 'exporting',
  IN_BASKET_MESSAGES: 'in_basket_messages',
  IN_MEETING: 'in_meeting',
  ON_BREAK: 'on_break',
  TRIAGE: 'triage',
  OFFLINE: 'offline',
} as const;

export type OmniPresence = (typeof USER_PRESENCE_STATUS)[keyof typeof USER_PRESENCE_STATUS];

export const PRESENCE_META: Record<OmniPresence, { label: string; color: string }> = {
  available: { label: 'Available', color: '#2E9E8F' },
  busy: { label: 'Busy', color: '#E03131' },
  emr_workflow: { label: 'EMR / Workflow', color: '#C92A2A' },
  exporting: { label: 'Exporting', color: '#D9480F' },
  in_basket_messages: { label: 'In Basket Messages', color: '#E8590C' },
  in_meeting: { label: 'In Meeting', color: '#F59F00' },
  on_break: { label: 'On Break', color: '#FAB005' },
  triage: { label: 'Triage', color: '#AE3EC9' },
  offline: { label: 'Offline', color: '#868E96' },
};

export const BUSY_PRESENCES: OmniPresence[] = [
  USER_PRESENCE_STATUS.BUSY,
  USER_PRESENCE_STATUS.EMR_WORKFLOW,
  USER_PRESENCE_STATUS.EXPORTING,
  USER_PRESENCE_STATUS.IN_BASKET_MESSAGES,
  USER_PRESENCE_STATUS.IN_MEETING,
  USER_PRESENCE_STATUS.ON_BREAK,
  USER_PRESENCE_STATUS.TRIAGE,
];

export interface MeResponseDto {
  id: string;
  given: string;
  family: string;
  email: string;
  role: string;
  presenceStatus: OmniPresence;
  shouldBeOfferedTasks: boolean;
  assignedTasks: TaskResponseDto[];
  offeredTasks: TaskResponseDto[];
}

export interface TaskOfferDto {
  id: string;
  taskId: string;
  workType: string;
  expiresAt: string | null;
  offeredAt: string;
}

export interface TaskOffersResponseDto {
  items: TaskOfferDto[];
}

export interface UpdatePresenceStatusDto {
  presenceStatus: OmniPresence;
}

export const meApiSlice = createApi({
  reducerPath: 'meApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set('authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ['Me', 'TaskOffers'],
  endpoints: (builder) => ({
    getMe: builder.query<MeResponseDto, void>({
      query: () => '/me',
      providesTags: [{ type: 'Me', id: 'CURRENT' }],
    }),
    updatePresenceStatus: builder.mutation<MeResponseDto, UpdatePresenceStatusDto>({
      query: (body) => ({ url: '/me/presence-status', method: 'PATCH', body }),
      invalidatesTags: [{ type: 'Me', id: 'CURRENT' }],
    }),
    getTaskOffers: builder.query<TaskOffersResponseDto, void>({
      query: () => '/me/task-offers',
      providesTags: ['TaskOffers'],
      transformResponse: (res: TaskOffersResponseDto | TaskOfferDto[]) =>
        Array.isArray(res) ? { items: res } : res,
    }),
    recordHeartbeat: builder.mutation<void, void>({
      query: () => ({ url: '/me/heartbeat', method: 'POST' }),
    }),
    acceptTaskOffer: builder.mutation<void, { taskId: string; expectedRouteGeneration: number }>({
      query: ({ taskId, expectedRouteGeneration }) => ({
        url: `/tasks/${taskId}/accept`,
        method: 'POST',
        body: { expectedRouteGeneration },
      }),
      invalidatesTags: ['TaskOffers'],
    }),
    declineTaskOffer: builder.mutation<void, { taskId: string; expectedRouteGeneration: number }>({
      query: ({ taskId, expectedRouteGeneration }) => ({
        url: `/tasks/${taskId}/decline`,
        method: 'POST',
        body: { expectedRouteGeneration },
      }),
      invalidatesTags: ['TaskOffers'],
    }),
  }),
});

export const {
  useGetMeQuery,
  useUpdatePresenceStatusMutation,
  useGetTaskOffersQuery,
  useRecordHeartbeatMutation,
  useAcceptTaskOfferMutation,
  useDeclineTaskOfferMutation,
} = meApiSlice;

export interface MessagesStreamEvent {
  id?: string;
  type: string;
  data: unknown;
}

const HEARTBEAT_INTERVAL_MS = 60_000;

export function useOmniHeartbeat(enabled: boolean) {
  const [recordHeartbeat] = useRecordHeartbeatMutation();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        await recordHeartbeat().unwrap();
      } catch {
        // silent — heartbeat is best-effort
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        intervalRef.current ??= setInterval(tick, HEARTBEAT_INTERVAL_MS);
      } else {
        stop();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    if (document.visibilityState === 'visible') {
      tick();
      intervalRef.current = setInterval(tick, HEARTBEAT_INTERVAL_MS);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stop();
    };
  }, [enabled, recordHeartbeat]);
}

const WS_URL = import.meta.env.VITE_APP_WS_URL as string | undefined;

const WS_RETRY_BASE_MS = 2_000;
const WS_RETRY_MAX_MS = 60_000;

export function useMessagesStream(enabled: boolean, onTaskOffered?: () => void, onTaskAssigned?: () => void) {
  const token = useAppSelector((state: RootState) => state.auth.token);
  const currentUserId = useAppSelector((state: RootState) => state.auth.user?.id);
  const dispatch = useAppDispatch();
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  const onTaskOfferedRef = useRef(onTaskOffered);
  onTaskOfferedRef.current = onTaskOffered;
  const onTaskAssignedRef = useRef(onTaskAssigned);
  onTaskAssignedRef.current = onTaskAssigned;

  useEffect(() => {
    if (!enabled || !token || !WS_URL) return;

    // Local flag per effect run — avoids the stale-closure reconnect leak that
    // occurs when a shared ref is reset to false by the new run before the old
    // socket's async onclose fires.
    let destroyed = false;
    let retryDelay = WS_RETRY_BASE_MS;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let ws: WebSocket | null = null;

    const connect = () => {
      if (destroyed) return;

      ws = new WebSocket(WS_URL!);

      ws.onopen = () => {
        retryDelay = WS_RETRY_BASE_MS;
        ws?.send(JSON.stringify({ type: 'auth', token }));
      };

      ws.onmessage = (event) => {
        try {
          const frame = JSON.parse(event.data as string);
          if (frame.comment === 'keepalive') return;

          // frame.data may already be an object on some server versions
          const payload = typeof frame.data === 'string' ? JSON.parse(frame.data) : frame.data;
          if (import.meta.env.DEV) console.debug('[ws]', frame.type, frame.id);

          const myId = currentUserIdRef.current;

          switch (frame.type) {
            case 'task_offered':
              if (payload.userId === myId) {
                dispatch(meApiSlice.endpoints.getTaskOffers.initiate(undefined, { forceRefetch: true }));
                onTaskOfferedRef.current?.();
              }
              break;
            case 'task_assigned':
              if (payload.userId === myId) {
                dispatch(meApiSlice.endpoints.getTaskOffers.initiate(undefined, { forceRefetch: true }));
                dispatch(meApiSlice.endpoints.getMe.initiate(undefined, { forceRefetch: true }));
                onTaskAssignedRef.current?.();
              }
              break;
            case 'task_offer_rescinded':
              if (payload.userId === myId) {
                dispatch(meApiSlice.endpoints.getTaskOffers.initiate(undefined, { forceRefetch: true }));
              }
              break;
            case 'task_unassigned':
            case 'task_completed':
            case 'task_canceled':
              if (payload.userId === myId) {
                dispatch(meApiSlice.endpoints.getTaskOffers.initiate(undefined, { forceRefetch: true }));
                dispatch(meApiSlice.endpoints.getMe.initiate(undefined, { forceRefetch: true }));
              }
              break;
            case 'user_presence_status_changed':
              dispatch(meApiSlice.endpoints.getMe.initiate(undefined, { forceRefetch: true }));
              break;
          }
        } catch (err) {
          if (import.meta.env.DEV) console.warn('[ws] failed to parse frame', err);
        }
      };

      ws.onclose = (event) => {
        if (destroyed) return;
        // 1008 = auth rejected — don't retry
        if (event.code === 1008) {
          if (import.meta.env.DEV) console.error('[ws] auth rejected, not retrying');
          return;
        }
        retryTimer = setTimeout(() => {
          retryDelay = Math.min(retryDelay * 2, WS_RETRY_MAX_MS);
          connect();
        }, retryDelay);
      };

      ws.onerror = () => {
        // onclose fires after onerror, reconnect is handled there
      };
    };

    connect();

    return () => {
      destroyed = true;
      if (retryTimer !== null) clearTimeout(retryTimer);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close(1000, 'unmount');
      } else {
        ws?.close();
      }
    };
  }, [enabled, token, dispatch]);
}
